import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Loader2, Shield } from "lucide-react";
import { AuthShell } from "@/components/auth/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSuperadminLogin, useSuperadminSession } from "@/hooks/use-auth";
import { authErrorMessage } from "@/lib/auth-errors";

export default function SuperadminLoginPage() {
  const navigate = useNavigate();
  const login = useSuperadminLogin();
  const { data: alreadyIn, isLoading } = useSuperadminSession();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  if (!isLoading && alreadyIn) {
    return <Navigate to="/superadmin" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!username || !password || login.isPending) return;
    try {
      await login.mutateAsync({ username, password });
      navigate("/superadmin", { replace: true });
    } catch {
      /* surfaced via login.error */
    }
  }

  const err = authErrorMessage(login.error);
  const canSubmit = username.length > 0 && password.length > 0 && !login.isPending;

  return (
    <AuthShell tone="subdued">
      <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-6 shadow-card backdrop-blur md:p-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/50 px-2.5 py-0.5 text-xs uppercase tracking-widest text-muted-foreground">
          <Shield className="h-3 w-3" strokeWidth={2.25} />
          Superadmin
        </div>
        <h1 className="mt-4 font-display text-3xl font-black tracking-tight">
          Platform access
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          For platform administrators only. Uses separate credentials.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              autoComplete="username"
              autoFocus
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={login.isPending}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sa-password">Password</Label>
            <div className="relative">
              <Input
                id="sa-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={login.isPending}
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 grid w-9 place-items-center text-muted-foreground transition-colors hover:text-foreground"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {err && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="group w-full"
            disabled={!canSubmit}
          >
            {login.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Signing in…
              </>
            ) : (
              <>
                Sign in
                <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </form>
      </div>

      <div className="mt-6 text-center text-xs text-muted-foreground">
        Looking for workspace access?{" "}
        <Link
          to="/login"
          className="font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          Workspace sign in
        </Link>
      </div>
    </AuthShell>
  );
}
